import { type ComponentProps, type ReactNode, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { PopoverContent } from '@apps/web/components/ui/popover';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    AUTO_APPROVAL_RULES_LABEL,
    AUTO_APPROVAL_RULES_MAX_LENGTH,
    AUTO_APPROVAL_RULES_PLACEHOLDER,
} from '@apps/web/features/phoebe_chat_v3/model/approval_rules';

export type ApprovalRulesPopoverProps = Readonly<{
    readonly rules: string | null;
    readonly canSave: boolean;
    readonly saveLabel?: string;
    readonly onCloseAutoFocus?: ComponentProps<
        typeof PopoverContent
    >['onCloseAutoFocus'];
    readonly onFocusOutside?: ComponentProps<
        typeof PopoverContent
    >['onFocusOutside'];
    readonly onInteractOutside?: ComponentProps<
        typeof PopoverContent
    >['onInteractOutside'];
    readonly onSave: (text: string) => void;
    readonly onCancel: () => void;
}>;

export function ApprovalRulesPopover({
    rules,
    canSave,
    saveLabel = 'Save',
    onCloseAutoFocus,
    onFocusOutside,
    onInteractOutside,
    onSave,
    onCancel,
}: ApprovalRulesPopoverProps): ReactNode {
    const [draft, setDraft] = useState(rules ?? '');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const trimmedDraft = draft.trim();
    const showCounter = draft.length >= 1_800;

    return (
        <PopoverContent
            side="top"
            align="start"
            sideOffset={7}
            collisionPadding={8}
            className="w-[min(28rem,calc(100vw-1rem))] space-y-3 rounded-lg"
            onOpenAutoFocus={(event) => {
                event.preventDefault();
                const textarea = textareaRef.current;
                textarea?.focus();
                textarea?.setSelectionRange(
                    textarea.value.length,
                    textarea.value.length,
                );
            }}
            onCloseAutoFocus={onCloseAutoFocus}
            onFocusOutside={onFocusOutside}
            onInteractOutside={onInteractOutside}
            onEscapeKeyDown={onCancel}
        >
            <div className="space-y-1.5">
                <label
                    htmlFor="auto-approval-rules"
                    className="text-sm font-semibold"
                >
                    {AUTO_APPROVAL_RULES_LABEL}
                </label>
                <Textarea
                    ref={textareaRef}
                    id="auto-approval-rules"
                    value={draft}
                    maxLength={AUTO_APPROVAL_RULES_MAX_LENGTH}
                    readOnly={!canSave}
                    placeholder={AUTO_APPROVAL_RULES_PLACEHOLDER}
                    className="max-h-[40dvh] min-h-28 resize-y overflow-y-auto"
                    onChange={(event) => setDraft(event.target.value)}
                />
                {showCounter ? (
                    <p className="text-muted-foreground text-right text-xs tabular-nums">
                        {draft.length.toLocaleString()}/
                        {AUTO_APPROVAL_RULES_MAX_LENGTH.toLocaleString()}
                    </p>
                ) : null}
            </div>
            {canSave ? (
                <div className="flex justify-end">
                    <Button
                        type="button"
                        disabled={trimmedDraft.length === 0}
                        onClick={() => onSave(trimmedDraft)}
                    >
                        {saveLabel}
                    </Button>
                </div>
            ) : null}
        </PopoverContent>
    );
}
